/**
 * Service for hotel settings management API endpoints
 * Handles configuration for check-in/out times, rates, policies, and other hotel-wide settings
 */
import apiClient from './ApiClient';
import apiMonitor from '../utils/apiMonitor';

class HotelSettingsService {
  constructor() {
    this.cache = {
      settings: null,
      generalSettings: null,
      propertyInformation: null,
      financialSettings: null,
      operationalSettings: null,
      supportedCurrencies: null,
      paymentMethods: null,
      emailTemplates: null,
      backupSettings: null,
      sequenceSettings: null,
      lastFetch: new Map(),
      promises: new Map()
    };
    this.CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache for settings (rarely change)
  }

  /**
   * Generic cache handler for settings
   */
  async _getCachedData(key, apiCall, forceRefresh = false) {
    const now = Date.now();
    
    // Check cache
    const hasValidCache = this.cache[key] && 
                         this.cache.lastFetch.has(key) && 
                         (now - this.cache.lastFetch.get(key)) < this.CACHE_DURATION;
    
    if (!forceRefresh && hasValidCache) {
      // Log to monitor with appropriate endpoint
      const endpointMap = {
        'settings': '/v1/settings',
        'generalSettings': '/v1/settings/general',
        'propertyInformation': '/v1/settings/property',
        'financialSettings': '/v1/settings/financial',
        'operationalSettings': '/v1/settings/operational',
        'supportedCurrencies': '/v1/settings/currencies',
        'paymentMethods': '/v1/settings/payment-methods',
        'emailTemplates': '/v1/settings/email-templates',
        'backupSettings': '/v1/settings/backup'
      };
      apiMonitor.logRequest(endpointMap[key] || `/v1/settings/${key}`, 'GET', true, 'HotelSettingsService');
      return Promise.resolve(this.cache[key]);
    }

    // Check for ongoing request
    if (this.cache.promises.has(key)) {
      return this.cache.promises.get(key);
    }

    // Make new request
    // Log to monitor with appropriate endpoint
    const endpointMap = {
      'settings': '/v1/settings',
      'generalSettings': '/v1/settings/general',
      'propertyInformation': '/v1/settings/property',
      'financialSettings': '/v1/settings/financial',
      'operationalSettings': '/v1/settings/operational',
      'supportedCurrencies': '/v1/settings/currencies',
      'paymentMethods': '/v1/settings/payment-methods',
      'emailTemplates': '/v1/settings/email-templates',
      'backupSettings': '/v1/settings/backup'
    };
    apiMonitor.logRequest(endpointMap[key] || `/v1/settings/${key}`, 'GET', false, 'HotelSettingsService');
    const promise = apiCall()
      .then(response => {
        this.cache[key] = response;
        this.cache.lastFetch.set(key, now);
        if (key === 'settings' && response) {
          // Persist the business-date clock for the synchronous
          // helpers in timezoneUtils (getBusinessDateSync /
          // getCheckInBusinessDateSync), which run during render and
          // can't await this call.
          try {
            localStorage.setItem('hotel_business_clock', JSON.stringify({
              nightAuditTime: response.nightAuditTime || null,
              checkInGraceHours: response.checkInGraceHours ?? null,
            }));
          } catch (e) { /* storage unavailable — fallbacks apply */ }
        }
        return response;
      })
      .catch(error => {
        console.error(`HotelSettingsService: Error fetching ${key}:`, error);
        throw error;
      })
      .finally(() => {
        this.cache.promises.delete(key);
      });

    this.cache.promises.set(key, promise);
    return promise;
  }

  /**
   * Clear cache
   */
  clearCache(key = null) {
    if (key) {
      this.cache[key] = null;
      this.cache.lastFetch.delete(key);
    } else {
      Object.keys(this.cache).forEach(k => {
        if (typeof this.cache[k] !== 'object' || this.cache[k] === null) {
          this.cache[k] = null;
        }
      });
      this.cache.lastFetch.clear();
      this.cache.promises.clear();
    }
  }

  /**
   * Get hotel settings
   * @param {boolean} forceRefresh - Force refresh from API
   * @returns {Promise} Promise resolving to hotel settings object
   */
  async getSettings(forceRefresh = false) {
    return this._getCachedData('settings', () => apiClient.get('/v1/settings'), forceRefresh);
  }

  /**
   * Update hotel settings
   * @param {Object} settingsData - Settings data to update
   * @param {string} settingsData.checkInTime - Check-in time (HH:MM format)
   * @param {string} settingsData.checkOutTime - Check-out time (HH:MM format)
   * @param {number} settingsData.lateCheckoutFee - Late checkout fee
   * @param {number} settingsData.maxRoomsPerReservation - Maximum rooms per reservation
   * @param {number} settingsData.overbookingThreshold - Overbooking threshold
   * @param {number} settingsData.taxRate - Tax rate percentage
   * @param {string} settingsData.defaultCurrency - Default currency code
   * @param {number} settingsData.reservationExpiryHours - Reservation expiry hours
   * @param {Array<string>} settingsData.supportedCurrencies - Supported currency codes
   * @param {Array<string>} settingsData.allowedPaymentMethods - Allowed payment methods
   * @returns {Promise} Promise resolving to updated settings object
   */
  async updateSettings(settingsData) {
    const result = await apiClient.put('/settings', settingsData);
    this.clearCache('settings');
    return result;
  }

  /**
   * Get general settings section
   * @param {boolean} forceRefresh - Force refresh from API
   * @returns {Promise} Promise resolving to general settings
   */
  async getGeneralSettings(forceRefresh = false) {
    return this._getCachedData('generalSettings', () => apiClient.get('/v1/settings/general'), forceRefresh);
  }

  /**
   * Update general settings section
   * @param {Object} generalSettings - General settings data
   * @returns {Promise} Promise resolving to updated general settings
   */
  async updateGeneralSettings(generalSettings) {
    const result = await apiClient.put('/v1/settings/general', generalSettings);
    this.clearCache('generalSettings');
    return result;
  }

  /**
   * Get financial settings section
   * @param {boolean} forceRefresh - Force refresh from API
   * @returns {Promise} Promise resolving to financial settings
   */
  async getFinancialSettings(forceRefresh = false) {
    return this._getCachedData('financialSettings', () => apiClient.get('/v1/settings/financial'), forceRefresh);
  }

  /**
   * Update financial settings section
   * @param {Object} financialSettings - Financial settings data
   * @returns {Promise} Promise resolving to updated financial settings
   */
  async updateFinancialSettings(financialSettings) {
    const result = await apiClient.put('/settings/financial', financialSettings);
    this.clearCache('financialSettings');
    return result;
  }

  /**
   * Get operational settings section
   * @param {boolean} forceRefresh - Force refresh from API
   * @returns {Promise} Promise resolving to operational settings
   */
  async getOperationalSettings(forceRefresh = false) {
    return this._getCachedData('operationalSettings', () => apiClient.get('/v1/settings/operational'), forceRefresh);
  }

  /**
   * Update operational settings section
   * @param {Object} operationalSettings - Operational settings data
   * @returns {Promise} Promise resolving to updated operational settings
   */
  async updateOperationalSettings(operationalSettings) {
    const result = await apiClient.put('/settings/operational', operationalSettings);
    this.clearCache('operationalSettings');
    return result;
  }

  /**
   * Get supported currencies
   * @param {boolean} forceRefresh - Force refresh from API
   * @returns {Promise} Promise resolving to supported currencies array
   */
  async getSupportedCurrencies(forceRefresh = false) {
    return this._getCachedData('supportedCurrencies', () => apiClient.get('/v1/settings/currencies'), forceRefresh);
  }

  /**
   * Update supported currencies
   * @param {Array<string>} currencies - Array of currency codes
   * @returns {Promise} Promise resolving to updated currencies
   */
  async updateSupportedCurrencies(currencies) {
    const result = await apiClient.put('/settings/currencies', { currencies });
    this.clearCache('supportedCurrencies');
    return result;
  }

  /**
   * Get hotel language settings (default + supported + available).
   * @param {boolean} forceRefresh
   * @returns {Promise<{defaultLanguage:string, supportedLanguages:string[], availableLanguages:Array<{code:string,label:string}>}>}
   */
  async getLanguageSettings(forceRefresh = false) {
    return this._getCachedData('languageSettings', () => apiClient.get('/v1/settings/languages'), forceRefresh);
  }

  /**
   * Update hotel language settings.
   * @param {{defaultLanguage:string, supportedLanguages:string[]}} payload
   */
  async updateLanguageSettings(payload) {
    const result = await apiClient.patch('/v1/settings/languages', {
      defaultLanguage: payload.defaultLanguage,
      supportedLanguages: payload.supportedLanguages,
    });
    this.clearCache('languageSettings');
    return result;
  }

  /**
   * Get payment methods
   * @param {boolean} forceRefresh - Force refresh from API
   * @returns {Promise} Promise resolving to payment methods array
   */
  async getPaymentMethods(forceRefresh = false) {
    return this._getCachedData('paymentMethods', () => apiClient.get('/v1/settings/payment-methods'), forceRefresh);
  }

  /**
   * Update payment methods
   * @param {Array<Object>} paymentMethods - Array of payment method objects
   * @returns {Promise} Promise resolving to updated payment methods
   */
  async updatePaymentMethods(paymentMethods) {
    const result = await apiClient.put('/settings/payment-methods', { paymentMethods });
    this.clearCache('paymentMethods');
    return result;
  }

  /**
   * Get email templates
   * @param {boolean} forceRefresh - Force refresh from API
   * @returns {Promise} Promise resolving to email templates
   */
  async getEmailTemplates(forceRefresh = false) {
    return this._getCachedData('emailTemplates', () => apiClient.get('/v1/settings/email-templates'), forceRefresh);
  }

  /**
   * Update email template
   * @param {string} templateId - Template ID
   * @param {Object} templateData - Template data
   * @returns {Promise} Promise resolving to updated template
   */
  async updateEmailTemplate(templateId, templateData) {
    const result = await apiClient.put(`/settings/email-templates/${templateId}`, templateData);
    this.clearCache('emailTemplates');
    return result;
  }

  /**
   * Get hotel property information
   * @param {boolean} forceRefresh - Force refresh from API
   * @returns {Promise} Promise resolving to hotel property information
   */
  async getPropertyInformation(forceRefresh = false) {
    return this._getCachedData('propertyInformation', () => apiClient.get('/v1/settings/property'), forceRefresh);
  }

  /**
   * Update hotel property information
   * @param {Object} propertyData - Property data
   * @returns {Promise} Promise resolving to updated property information
   */
  async updatePropertyInformation(propertyData) {
    const result = await apiClient.put('/settings/property', propertyData);
    this.clearCache('propertyInformation');
    return result;
  }

  /**
   * Get system backup settings
   * @param {boolean} forceRefresh - Force refresh from API
   * @returns {Promise} Promise resolving to backup settings
   */
  async getBackupSettings(forceRefresh = false) {
    return this._getCachedData('backupSettings', () => apiClient.get('/v1/settings/backup'), forceRefresh);
  }

  /**
   * Update system backup settings
   * @param {Object} backupSettings - Backup settings
   * @returns {Promise} Promise resolving to updated backup settings
   */
  async updateBackupSettings(backupSettings) {
    const result = await apiClient.put('/settings/backup', backupSettings);
    this.clearCache('backupSettings');
    return result;
  }

  /**
   * Trigger system backup
   * @returns {Promise} Promise resolving to backup result
   */
  async triggerBackup() {
    return apiClient.post('/settings/backup/trigger');
  }

  /**
   * Get sequence numbering settings (folio + invoice)
   */
  async getSequenceSettings(forceRefresh = false) {
    return this._getCachedData('sequenceSettings', () => apiClient.get('/v1/settings/sequences'), forceRefresh);
  }

  /**
   * Update sequence numbering settings
   */
  async updateSequenceSettings(data) {
    const result = await apiClient.put('/v1/settings/sequences', data);
    this.clearCache('sequenceSettings');
    return result;
  }

  /**
   * Update a folio's number (manual override)
   */
  async updateFolioNumber(folioId, number) {
    return apiClient.patch(`/v1/folios/${folioId}/number`, { folioNumber: number });
  }

  /**
   * Update an invoice's number (manual override)
   */
  async updateInvoiceNumber(invoiceId, number) {
    return apiClient.patch(`/v1/invoices/${invoiceId}/number`, { invoiceNumber: number });
  }

  // ─── Document Template Methods ───────────────────────────

  /**
   * Get all document templates (returns defaults if none saved)
   */
  async getTemplates() {
    return apiClient.get('/v1/settings/templates');
  }

  /**
   * Get a single template by type and language
   * @param {string} type - registration_card, invoice, confirmation_email, or confirmation_form
   * @param {string} language - BCP-47 language code, defaults to 'en'
   */
  async getTemplate(type, language = 'en') {
    return apiClient.get(`/v1/settings/templates/${type}?language=${language}`);
  }

  /**
   * Save/update a template
   * @param {string} type - Template type
   * @param {Object} data - { name, content, termsText }
   */
  async updateTemplate(type, data) {
    return apiClient.put(`/v1/settings/templates/${type}`, data);
  }

  /**
   * Reset template to default for a specific language
   * @param {string} type - Template type
   * @param {string} language - BCP-47 language code, defaults to 'en'
   */
  async resetTemplate(type, language = 'en') {
    return apiClient.post(`/v1/settings/templates/${type}/reset`, { language });
  }

  /**
   * Preview a template with sample data
   * @param {string} type - Template type
   * @param {Object} data - { content, termsText, language }
   * @returns {Promise} { rendered, format }
   */
  async previewTemplate(type, data) {
    return apiClient.post(`/v1/settings/templates/${type}/preview`, data);
  }

  /**
   * Get available placeholder codes for templates
   * @param {string} type - Optional template type to filter codes
   */
  /** Version history of one template variant (newest first). */
  async getTemplateRevisions(type, language = 'en') {
    return apiClient.get(`/v1/settings/templates/${type}/revisions?language=${encodeURIComponent(language)}`);
  }

  /** One history entry including its full content. */
  async getTemplateRevision(revisionId) {
    return apiClient.get(`/v1/settings/template-revisions/${revisionId}`);
  }

  /** Make a history entry the live template again (itself recorded). */
  async restoreTemplateRevision(revisionId) {
    return apiClient.post(`/v1/settings/template-revisions/${revisionId}/restore`, {});
  }

  async getTemplateCodes(type = null) {
    const params = type ? `?type=${type}` : '';
    return apiClient.get(`/v1/settings/template-codes${params}`);
  }
}

// Create and export a singleton instance
const hotelSettingsService = new HotelSettingsService();
export default hotelSettingsService;